import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import { selectPermissions } from '@redux/accounts';
import {
  availableCandidatesSelectors,
  fetchAvailableCandidates,
} from '@redux/availableCandidates';
import {
  applyAvailableCandidateFilters,
  candidateDetailsPath,
  collectAvailableFilterOptions,
  isStatusForbidden,
  isStatusIdle,
  isStatusLoading,
} from '@utils';
import { AppRoutes, CandidateDetailsAction, hasPermission } from '@constants';
import {
  useAvailableCandidateActions,
  useAvailableCandidatesViewState,
  useIsPhone,
  useTitle,
} from '@hooks';
import { PageTitle } from 'constants/pageTitle';
import { AskQuestionDialog, NotAllowed, PageLoader } from '@components';
import { NoMatchesState } from 'components/common/NoMatchesState/NoMatchesState';
import { ListPage } from 'components/candidate/CandidatesList/components/ListPage';
import { LIST_FILTERS_DIALOG } from 'components/candidate/CandidatesList/components/ListFiltersDialog';
import { ClientFeedbackDialog } from 'components/candidate/ClientFeedbackModal/ClientFeedbackDialog';
import { defaultColumnIds } from 'components/candidate/CandidatesList/columns';
import { AvailableCandidateColumns } from 'components/availableCandidates/columns';
import { AvailableCandidateDrawer } from 'components/availableCandidates/AvailableCandidateDrawer';
import { AvailableCandidatesHeader } from 'components/availableCandidates/AvailableCandidatesHeader';
import { AvailableCandidatesList } from 'components/availableCandidates/AvailableCandidatesList';
import { AvailableCandidatesSearchForm } from 'components/availableCandidates/AvailableCandidatesSearchForm';
import { AvailableCandidatesToolbar } from 'components/availableCandidates/AvailableCandidatesToolbar';
import { MoreProfilesLine } from 'components/availableCandidates/MoreProfilesLine';

export const AvailableCandidatesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search, state: locationState } = useLocation();
  const isPhone = useIsPhone();
  const setPageTitle = useTitle();

  const [openCandidateId, setOpenCandidateId] = useState<string | null>(null);

  const selectedAccountId = useAppSelector((state) => state.accounts.selected);
  const arePermissionsLoading = useAppSelector(
    (state) => state.accounts.loading,
  );
  const canRead = hasPermission(useAppSelector(selectPermissions), 'N:R');
  const { apiStatus, data } = useAppSelector(
    availableCandidatesSelectors.getAvailableCandidatesData,
  );

  useEffect(() => {
    setPageTitle(PageTitle.AVAILABLE_CANDIDATES_PAGE);
  }, [setPageTitle]);

  const defaultColumns = useMemo(
    () => defaultColumnIds(AvailableCandidateColumns, data),
    [data],
  );
  const { state, write, toggleSort, reset, activeFilterCount, isDefault } =
    useAvailableCandidatesViewState(defaultColumns);
  const isSearching = Boolean(state.query.trim()) || activeFilterCount > 0;

  useEffect(() => {
    if (canRead) dispatch(fetchAvailableCandidates());
  }, [dispatch, selectedAccountId, canRead]);
  const listed = useMemo(
    () =>
      state.showSkipped ? data : data.filter(({ mark }) => mark !== 'skip'),
    [data, state.showSkipped],
  );
  const filterOptions = useMemo(
    () => collectAvailableFilterOptions(listed),
    [listed],
  );
  const shown = useMemo(
    () =>
      applyAvailableCandidateFilters(
        listed,
        state.query,
        state,
        filterOptions.seniorityScale,
      ),
    [listed, state, filterOptions.seniorityScale],
  );
  const { onAction, onLearnMore, onOpenResume, dialogProps, questionProps } =
    useAvailableCandidateActions();

  const onCandidateClick = useCallback(
    (id: string) => {
      if (isPhone) {
        navigate(
          candidateDetailsPath(id, search, AppRoutes.AVAILABLE_CANDIDATES),
        );
        return;
      }

      setOpenCandidateId(id);
    },
    [isPhone, navigate, search],
  );

  const onDrawerAction = (id: string, action: CandidateDetailsAction) => {
    onAction(id, action);
    if (action === CandidateDetailsAction.SKIP_CANDIDATE && !state.showSkipped)
      setOpenCandidateId(null);
  };

  const isLoading =
    arePermissionsLoading ||
    isStatusLoading(apiStatus) ||
    (canRead && isStatusIdle(apiStatus));

  if (!arePermissionsLoading && !canRead) return <NotAllowed />;
  if (isLoading && !data.length) return <PageLoader />;
  if (isStatusForbidden(apiStatus)) return <NotAllowed />;

  const noneListed = (
    <NoMatchesState
      title="No available candidates right now"
      description="We'll list AgileEngineers and pre-screened candidates here as they become available"
    />
  );

  const searchForm = (
    <AvailableCandidatesSearchForm
      key={[
        state.seniorities,
        state.regions,
        state.primarySkills,
        state.skills,
      ].join('|')}
      options={filterOptions}
      filters={state}
      onApply={write}
    />
  );

  const controls = {
    candidates: listed,
    filterOptions,
    view: state,
    activeFilterCount,
    skippedCount: data.filter(({ mark }) => mark === 'skip').length,
    isDefault,
    onChange: write,
    onReset: reset,
  };

  if (
    data.length &&
    !isSearching &&
    locationState?.dialog !== LIST_FILTERS_DIALOG
  )
    return (
      <ListPage overlays={null} plain>
        {searchForm}
      </ListPage>
    );

  return (
    <ListPage
      overlays={
        <>
          {!isPhone && (
            <AvailableCandidateDrawer
              candidate={data.find(({ id }) => id === openCandidateId) ?? null}
              onClose={() => setOpenCandidateId(null)}
              onOpenFullPage={(id) =>
                navigate(
                  candidateDetailsPath(
                    id,
                    search,
                    AppRoutes.AVAILABLE_CANDIDATES,
                  ),
                )
              }
              onAction={onDrawerAction}
              onLearnMore={onLearnMore}
              onOpenResume={onOpenResume}
            />
          )}

          <ClientFeedbackDialog {...dialogProps} />
          <AskQuestionDialog {...questionProps} />
        </>
      }
    >
      <AvailableCandidatesHeader
        shown={shown}
        controls={data.length ? controls : undefined}
      />

      {data.length ? (
        <>
          <AvailableCandidatesToolbar {...controls} />

          <AvailableCandidatesList
            candidates={shown}
            view={state}
            emptyState={searchForm}
            onSort={toggleSort}
            onCandidateClick={onCandidateClick}
            onAction={onAction}
            onLearnMore={onLearnMore}
            onOpenResume={onOpenResume}
          />

          {shown.length > 0 && <MoreProfilesLine />}
        </>
      ) : (
        noneListed
      )}
    </ListPage>
  );
};
